import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Copy, KeyRound } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";

export default function MandatoryTwoFactorSetup() {
  const { user, setUser, logout } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [saved, setSaved] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    api.post("/auth/2fa/setup").then(({ data }) => setSetup(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const confirm = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/confirm", { code });
      setBackupCodes(data.backup_codes || []);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const finish = () => setUser({ ...user, totp_enabled: true });

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-[#1F2937] font-sans flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6" data-testid="mandatory-2fa">
          <div className="flex items-center gap-2 text-teal-600">
            <ShieldCheck className="w-5 h-5" />
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Secure your account</h1>
          </div>
          {!backupCodes ? (
            <>
              <p className="text-sm mt-1 text-slate-500">Two-factor authentication is required. Scan the QR in Google Authenticator, then enter the 6-digit code.</p>
              {!setup ? <div className="mt-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
                <div className="mt-4 space-y-3">
                  <img data-testid="mandatory-2fa-qr" src={`data:image/png;base64,${setup.qr_png_base64}`} alt="2FA QR" className="w-44 h-44 rounded-lg bg-white p-2 border border-slate-200 mx-auto" />
                  <div>
                    <Label className="text-xs text-slate-500">Or enter this key manually</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs font-mono break-all" data-testid="mandatory-2fa-secret">{setup.secret}</code>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Enter the 6-digit code</Label>
                    <Input data-testid="mandatory-2fa-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="mt-1 w-44 tracking-[0.3em] font-mono bg-white border-slate-300" />
                  </div>
                  <Button data-testid="mandatory-2fa-confirm" onClick={confirm} disabled={busy || code.length !== 6} className="bg-teal-600 hover:bg-teal-700 text-white w-full h-11">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enable & Continue"}</Button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-4 space-y-3" data-testid="mandatory-2fa-backup">
              <div className="flex items-center gap-2 text-slate-900"><KeyRound className="w-4 h-4 text-amber-600" /><h2 className="font-bold">Save your backup codes</h2></div>
              <p className="text-sm text-slate-500">Store these one-time backup codes somewhere safe. Each works once if you lose your authenticator.</p>
              <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                {backupCodes.map((c) => <code key={c} className="text-sm font-mono text-slate-800" data-testid="backup-code">{c}</code>)}
              </div>
              <Button size="sm" variant="outline" className="h-8" onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5 mr-1" />Copy all</Button>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="backup-saved-check" /> I have saved my backup codes</label>
              <Button data-testid="mandatory-2fa-finish" onClick={finish} disabled={!saved} className="bg-teal-600 hover:bg-teal-700 text-white w-full h-11">Continue to dashboard</Button>
            </div>
          )}
          <button onClick={logout} className="mt-4 w-full text-center text-xs text-slate-500 hover:text-slate-700">Log out</button>
        </div>
      </div>
    </div>
  );
}
